import { IconLoader2 } from "@tabler/icons-react"
import { type FC, forwardRef, type ReactNode } from "react"

import { cn, tw } from "@forento/ui/utilities/tailwind"

import Link from "~/components/Link"

export type ButtonVariant = "primary" | "secondary" | "primary-danger" | "secondary-danger" | "link"

interface ButtonProps {
	variant?: ButtonVariant
	onClick?: string | (() => void)
	newTab?: boolean
	isLoading?: boolean
	isDisabled?: boolean
	replaceOnLoading?: boolean
	/** Accessible name for buttons whose content is only an icon. */
	ariaLabel?: string
	className?: string
	children: ReactNode
}

interface SubmitButtonProps {
	variant?: ButtonVariant
	isLoading?: boolean
	isDisabled?: boolean
	replaceOnLoading?: boolean
	className?: string
	children: ReactNode
}

export const actionClass =
	"inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 rounded-lg border border-component-border bg-white px-3 py-1.5 font-medium text-sm text-gray-700 transition hover:bg-component-background focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:cursor-wait disabled:opacity-50"

const buttonClasses = tw`focus-visible:outline-primary flex cursor-pointer text-left text-black transition-colors duration-150 focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-auto`
const primaryClasses = tw`bg-primary hover:bg-primary/80 items-center justify-center gap-2 rounded-lg border border-transparent px-4 py-3 text-sm font-semibold text-white disabled:bg-gray-200 disabled:text-black`
const secondaryClasses = tw`border-primary/15 bg-primary/8 text-primary hover:bg-primary/10 items-center justify-center gap-2 rounded-lg border px-4 py-3 text-center text-sm font-semibold disabled:border-gray-200 disabled:bg-white disabled:text-gray-400`
const dangerClasses = tw`bg-danger hover:bg-danger/80 items-center justify-center gap-2 rounded-lg border border-transparent px-4 py-3 text-center text-sm font-semibold text-white disabled:bg-gray-200 disabled:text-black`
const dangerBorderedClasses = tw`border-danger/15 bg-danger/8 text-danger hover:bg-danger/10 items-center justify-center gap-2 rounded-lg border px-4 py-3 text-center text-sm font-semibold disabled:border-gray-200 disabled:bg-white disabled:text-gray-400`
const linkClasses = tw`text-primary inline-flex`

const Button = forwardRef<HTMLDivElement & HTMLButtonElement & HTMLAnchorElement, ButtonProps>(
	({ variant, onClick, newTab, isLoading, isDisabled, replaceOnLoading, ariaLabel, className, children }, ref) => {
		const content =
			isLoading && replaceOnLoading ? (
				<IconLoader2 className="size-6 animate-spin" />
			) : (
				<>
					{isLoading && <IconLoader2 className="size-3 animate-spin" />}
					{children}
				</>
			)

		const newTabProps = { target: "_blank", rel: "noreferrer noopener" }
		if (onClick === undefined)
			return (
				<button
					type="button"
					disabled={isDisabled || isLoading}
					ref={ref}
					aria-label={ariaLabel}
					aria-busy={isLoading || undefined}
					className={getButtonClasses(variant, className)}
				>
					{content}
				</button>
			)
		else if (typeof onClick === "function")
			return (
				<button
					type="button"
					onClick={onClick}
					disabled={isDisabled || isLoading}
					ref={ref}
					aria-label={ariaLabel}
					aria-busy={isLoading || undefined}
					className={getButtonClasses(variant, className)}
				>
					{content}
				</button>
			)
		else if (onClick?.startsWith("/"))
			return (
				<Link
					to={onClick}
					{...(newTab ? newTabProps : {})}
					ref={ref}
					aria-label={ariaLabel}
					aria-busy={isLoading || undefined}
					className={getButtonClasses(variant, className)}
				>
					{content}
				</Link>
			)
		else
			return (
				<a
					href={onClick}
					{...(newTab ? newTabProps : {})}
					ref={ref}
					aria-label={ariaLabel}
					aria-busy={isLoading || undefined}
					className={getButtonClasses(variant, className)}
				>
					{content}
				</a>
			)
	},
)

export const SubmitButton: FC<SubmitButtonProps> = ({
	variant,
	isLoading,
	isDisabled,
	replaceOnLoading,
	className,
	children,
}) => {
	if (isLoading) {
		return (
			<button type="submit" disabled className={getButtonClasses(variant, className)}>
				{isLoading && replaceOnLoading ? (
					<IconLoader2 className="size-6 animate-spin" />
				) : (
					<>
						{isLoading && <IconLoader2 className="size-3 animate-spin" />}
						{children}
					</>
				)}
			</button>
		)
	}

	return (
		<button type="submit" disabled={isDisabled} className={getButtonClasses(variant, className)}>
			{children}
		</button>
	)
}

function getButtonClasses(variant?: ButtonVariant, className?: string) {
	return cn(
		buttonClasses,
		variant === "primary" && primaryClasses,
		variant === "secondary" && secondaryClasses,
		variant === "primary-danger" && dangerClasses,
		variant === "secondary-danger" && dangerBorderedClasses,
		variant === "link" && linkClasses,
		className,
	)
}

export default Button
